<script lang="ts" generics="IdType extends (string | number)">
	import type { IDropdownOption } from "$lib/def/util-types";
	import { notifyOnClickOutside } from "$lib/util/ui";
	import { createEventDispatcher } from "svelte";

	export let options: IDropdownOption<IdType>[];
	export let defaultValue: IdType | undefined;
	export let placeholderText = "Select an option..."
	/** A name for this form control */
	export let name: string;
	/** The "text alignment" of the dropdown */
	export let orientation: "left" | "right" = "right";

	$: _selectedOptionId = options.find((opt) => {
		if (opt.id === defaultValue) return opt;
	})?.id;

	$: selectedOptionId = _selectedOptionId;

	const _options = options.map((opt) => ({
		...opt,
		title: opt.name ?? opt.title,
		/** Unique id for the input element */
		inputId: `${name}${opt.id}`,
	}));

	/** Notifies parent component which option has been selected */
	let dispatch = createEventDispatcher<Record<"selected", IdType>>();

	/** Is the dropdown open? */
	let showOptions = false;

	$: selectedOption = _options.find((opt) => opt.id === selectedOptionId);
	/** Dropdown should only be openable if there are many options to choose from */
	$: optionsAvailable = _options.length > 1 || !selectedOption;

	const onOptionSelected = (id: IdType) => {
		dispatch("selected", id);
		showOptions = false;
	};

	/** Modal behaviour: Close the options box if user clicks outside the box */
	const onClickOutside = (node: Node) => {
		return notifyOnClickOutside(node, () => { showOptions = false; });
	};

</script>

<div class="dropdown-container" use:onClickOutside style:text-align={orientation}>
	<button class="dropdown-control label-xl medium" on:click={() => { if (optionsAvailable) showOptions = true; }} data-testid="dropdown-control">
		{#if optionsAvailable}<span class="icon-switch-20 vertical-middle"></span>{/if}
		<span class="dropdown-label" data-testid="dropdown-label">{selectedOption?.title ?? placeholderText}</span>
	</button>

	{#if optionsAvailable && showOptions}
	<div class="dropdown-options shadow-elevation-24dp"
		style:right={ orientation === "right" ? "-4px" : "unset" }
		style:left={ orientation === "left" ? "0" : "unset" }
		data-testid="dropdown-options"
	>
		{#each _options as option}
			<div class="dropdown-input" data-testid={`dropdown-option-${option.id}`}>
				<div>
					<input type="radio" value={option.id} {name} id={option.inputId} bind:group={selectedOptionId} on:change={() => onOptionSelected(option.id) } />
					<label class="label-xl medium" for={option.inputId}>{option.title}</label>
				</div>
				<div><span class="icon-check-on-20" class:selected={option.id === selectedOptionId}></span></div>
			</div>
		{/each}
	</div>
	{/if}
</div>

<style lang="scss">
	@use 'color';

	.dropdown-container * {
		color: color.$on-background-high-importance;
	}
	
	.dropdown-input {
		padding: 12px;
		display: flex;
		justify-content: space-between;
		gap: 8px;

		label {
			display: inline;
			cursor: pointer;
		}

		input { display: none; }

		span.icon-check-on-20 { color: transparent; }
		span.icon-check-on-20.selected { color: color.$primary-variant; }
	}

	span.dropdown-label {
		border-bottom: 2px solid color.$on-background-low-importance;
		margin-left: 6px;
	}

	button.dropdown-control {
		background: none;
		margin-bottom: 0;
		padding: 10px 12px;
		border-radius: 100px;

		&:only-child {
			cursor: unset; // should not be a pointer because not clickable in this case
		}

		&:hover {
			background-color: color.$object-elevation-3dp;

			> span.dropdown-label {
				border-bottom: none;
			}
		}
	}

	div.dropdown-container {
		position: relative;
	}

	div.dropdown-options {
		position: absolute;
		top: 0;
		z-index: 3; // So that it appears in front of the combo tag
		width: max-content;
		background-color: color.$background-elevation-12dp;
		border-radius: 24px;
		padding: 12px;
	}
</style>